// Selector de objetos del bucket R2 (vía Worker /api/list). Muestra las
// "carpetas" (prefijos) y archivos del nivel actual, con navegación por
// breadcrumb. Al elegir un archivo devuelve su URL pública de R2.
import React, { useEffect, useMemo, useState } from 'react';
import { FileText, Film, Folder, FolderOpen, Home, Image as ImageIcon, Music, File as FileIcon, Loader2, Search, AlertTriangle, X, ChevronRight, ChevronLeft } from 'lucide-react';
import { listR2Objects, r2PublicUrl } from '../../services/mediaSigner';

const fmtSize = (bytes) => {
  if (!Number.isFinite(bytes) || bytes <= 0) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v.toFixed(v >= 100 ? 0 : 1)} ${units[i]}`;
};

const iconFor = (key) => {
  const k = String(key || '').toLowerCase();
  if (/\.(mp4|mov|webm|m4v|avi|mkv)$/.test(k)) return <Film size={15} style={{ color: '#38bdf8', flexShrink: 0 }} />;
  if (/\.(mp3|m4a|wav|ogg|aac|flac)$/.test(k)) return <Music size={15} style={{ color: '#34d399', flexShrink: 0 }} />;
  if (/\.(png|jpe?g|gif|webp|svg|bmp|ico)$/.test(k)) return <ImageIcon size={15} style={{ color: '#f59e0b', flexShrink: 0 }} />;
  if (/\.(pdf)$/.test(k)) return <FileText size={15} style={{ color: '#f87171', flexShrink: 0 }} />;
  return <FileIcon size={15} style={{ color: '#94a3b8', flexShrink: 0 }} />;
};

// Devuelve la última parte de un key/prefix (nombre de archivo o carpeta).
const basename = (key) => String(key || '').replace(/\/$/, '').split('/').pop() || key;

// Quita el último segmento de un prefijo ("a/b/" → "a/").
const parentOf = (prefix) => {
  const parts = String(prefix || '').replace(/\/$/, '').split('/');
  parts.pop();
  return parts.length ? `${parts.join('/')}/` : '';
};

export const R2ObjectPicker = ({ onPick, onClose }) => {
  const [prefix, setPrefix] = useState(''); // ruta actual: '' (raíz) o 'carpeta/sub/'
  const [folders, setFolders] = useState([]); // carpetas del nivel actual
  const [objects, setObjects] = useState([]); // archivos del nivel actual
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [cursor, setCursor] = useState('');
  const [truncated, setTruncated] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [query, setQuery] = useState('');

  const load = async (usePrefix, useCursor = '') => {
    try {
      setError('');
      const data = await listR2Objects({ prefix: usePrefix, delimiter: '/', cursor: useCursor, max: 1000 });
      if (useCursor) {
        setObjects((prev) => [...prev, ...(data.objects || [])]);
        setFolders((prev) => [...prev, ...(data.folders || [])]);
      } else {
        setObjects(data.objects || []);
        setFolders(data.folders || []);
      }
      setCursor(data.next || '');
      setTruncated(!!data.truncated);
    } catch (e) {
      setError(e.message || 'No se pudo listar el bucket.');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => { load(''); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  // Al cambiar de carpeta, recargar desde cero.
  const enterFolder = (folder) => {
    setLoading(true);
    setQuery('');
    setObjects([]);
    setFolders([]);
    setCursor('');
    setTruncated(false);
    setPrefix(folder);
    load(folder);
  };

  const goUp = () => enterFolder(parentOf(prefix));

  const crumbs = useMemo(() => {
    const parts = String(prefix || '').replace(/\/$/, '').split('/').filter(Boolean);
    const acc = [];
    let p = '';
    parts.forEach((part) => { p += `${part}/`; acc.push({ label: part, prefix: p }); });
    return acc;
  }, [prefix]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return { folders, objects };
    return {
      folders: folders.filter((f) => basename(f).toLowerCase().includes(q)),
      objects: objects.filter((o) => String(o.key || '').toLowerCase().includes(q)),
    };
  }, [folders, objects, query]);

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 10000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem',
      background: 'rgba(4,12,22,0.85)', backdropFilter: 'blur(10px)',
    }}>
      <div style={{
        background: '#0a1929', border: '1px solid rgba(197,160,89,0.3)', borderRadius: '18px',
        width: '100%', maxWidth: '720px', maxHeight: '88vh',
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 20px', borderBottom: '1px solid rgba(197,160,89,0.15)',
          background: '#0a1929',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ width: '34px', height: '34px', borderRadius: '9px', background: 'rgba(251,146,60,0.12)', border: '1px solid rgba(251,146,60,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FileIcon size={17} style={{ color: '#fb923c' }} />
            </div>
            <div>
              <h3 style={{ margin: 0, fontSize: '0.95rem', fontWeight: 700, color: '#fff' }}>Bucket R2 · Cloudflare</h3>
              <span style={{ fontSize: '0.68rem', color: '#64748b' }}>
                {folders.length} carpetas · {objects.length} archivos
              </span>
            </div>
          </div>
          <button type="button" onClick={onClose}  className="adm-btn adm-btn--secondary">
            <X size={20} />
          </button>
        </div>

        {/* Breadcrumb de navegación */}
        <div style={{ padding: '10px 20px', borderBottom: '1px solid rgba(197,160,89,0.12)', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => enterFolder('')}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', background: 'none', border: 'none', color: '#e2e8f0', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', padding: '4px 6px', borderRadius: '6px' }}
            title="Raíz del bucket"
          >
            <Home size={13} style={{ color: '#c5a059' }} /> Raíz
          </button>
          {crumbs.map((c) => (
            <React.Fragment key={c.prefix}>
              <ChevronRight size={12} style={{ color: '#475569' }} />
              <button
                type="button"
                onClick={() => enterFolder(c.prefix)}
                style={{ background: 'none', border: 'none', color: '#e2e8f0', fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer', padding: '4px 6px', borderRadius: '6px' }}
              >
                {c.label}
              </button>
            </React.Fragment>
          ))}
          {prefix && (
            <>
              <span style={{ flex: 1 }} />
              <button type="button" onClick={goUp}  style={{  display: 'inline-flex',  alignItems: 'center',  gap: '4px' }} className="adm-btn adm-btn--secondary">
                <ChevronLeft size={12} /> Subir
              </button>
            </>
          )}
        </div>

        <div style={{ padding: '12px 20px', borderBottom: '1px solid rgba(197,160,89,0.12)', display: 'flex', gap: '10px', alignItems: 'center' }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <Search size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#64748b' }} />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar por nombre en esta carpeta…"
              style={{
                width: '100%', background: 'rgba(7,21,38,0.8)', border: '1px solid rgba(197,160,89,0.25)',
                borderRadius: '10px', padding: '9px 12px 9px 32px', fontSize: '0.8rem', color: '#e2e8f0',
                outline: 'none', boxSizing: 'border-box', fontFamily: "'Inter', sans-serif",
              }}
            />
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '10px 14px' }}>
          {loading && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center', padding: '2rem 0', color: '#94a3b8', fontSize: '0.78rem' }}>
              <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Cargando objetos del bucket…
            </div>
          )}
          {error && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#f87171', fontSize: '0.76rem', padding: '1.2rem', border: '1px solid rgba(239,68,68,0.3)', borderRadius: '10px', background: 'rgba(239,68,68,0.06)' }}>
              <AlertTriangle size={15} /> {error}
            </div>
          )}
          {!loading && !error && filtered.folders.length === 0 && filtered.objects.length === 0 && (
            <p style={{ textAlign: 'center', color: '#64748b', fontSize: '0.78rem', padding: '2rem 0', fontStyle: 'italic' }}>
              {query ? 'Sin coincidencias en esta carpeta.' : (prefix ? 'Esta carpeta está vacía.' : 'El bucket está vacío.')}
            </p>
          )}
          {!loading && filtered.folders.map((folder) => (
            <button
              key={folder}
              type="button"
              onClick={() => enterFolder(folder)}
              style={{
                display: 'flex', alignItems: 'center', gap: '10px', width: '100%', textAlign: 'left',
                padding: '10px 12px', borderRadius: '10px', marginBottom: '6px', cursor: 'pointer',
                background: 'rgba(216,185,120,0.06)', border: '1px solid rgba(216,185,120,0.22)',
                color: '#e2e8f0', fontFamily: "'Inter', sans-serif",
              }}
            >
              <FolderOpen size={16} style={{ color: '#d8b978', flexShrink: 0 }} />
              <span style={{ flex: 1, fontSize: '0.78rem', fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {basename(folder)}
              </span>
              <ChevronRight size={14} style={{ color: '#d8b978', flexShrink: 0 }} />
            </button>
          ))}
          {!loading && filtered.objects.map((obj) => (
            <button
              key={obj.key}
              type="button"
              onClick={() => onPick(r2PublicUrl(obj.key), obj.key)}
              style={{
                display: 'flex', alignItems: 'center', gap: '10px', width: '100%', textAlign: 'left',
                padding: '10px 12px', borderRadius: '10px', marginBottom: '6px', cursor: 'pointer',
                background: 'rgba(7,21,38,0.5)', border: '1px solid rgba(197,160,89,0.15)',
                color: '#e2e8f0', fontFamily: "'Inter', sans-serif",
              }}
            >
              {iconFor(obj.key)}
              <span style={{ flex: 1, fontSize: '0.78rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {basename(obj.key)}
              </span>
              {obj.size ? <span style={{ fontSize: '0.66rem', color: '#64748b', flexShrink: 0 }}>{fmtSize(obj.size)}</span> : null}
              <ChevronRight size={14} style={{ color: '#c5a059', flexShrink: 0 }} />
            </button>
          ))}
          {truncated && (
            <div style={{ textAlign: 'center', padding: '8px 0' }}>
              <button
                type="button"
                disabled={loadingMore}
                onClick={() => { setLoadingMore(true); load(prefix, cursor); }}
                style={{
                  background: 'rgba(197,160,89,0.1)', color: '#c5a059', border: '1px solid rgba(197,160,89,0.3)',
                  borderRadius: '10px', padding: '8px 16px', fontSize: '0.75rem', fontWeight: 700, cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'center', gap: '7px',
                }}
              >
                {loadingMore ? <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> : null}
                Cargar más
              </button>
            </div>
          )}
        </div>

        <div style={{
          display: 'flex', justifyContent: 'flex-end', gap: '10px',
          padding: '14px 20px', borderTop: '1px solid rgba(197,160,89,0.15)',
          background: '#0a1929',
        }}>
          <button type="button" onClick={onClose}  className="adm-btn adm-btn--secondary">
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
};

export default R2ObjectPicker;
